import { DEFAULT_OUTCOME_COPY, DEFAULT_OUTCOME_COPY_FR } from './defaults';
import type {
  OutcomeContent,
  OutcomeCopyOverrides,
  OutcomeKey,
  PetLossLocale,
  ReadinessPageCopy,
  ReadinessPageOverrides
} from './types';
import { OUTCOME_KEYS } from './types';

const S3 = 'https://paction-shared-resources.s3.ca-central-1.amazonaws.com';
const APLB_URL = 'https://www.aplb.org/';
const APLB_LOGO_URL = '/icons/aplb_logo_text.png';

export const RAINBOW_BRIDGE_IMAGE_URL = `${S3}/common/pet-loss-rainbow-bridge.webp`;

export const DEFAULT_PAGE_COPY: Record<PetLossLocale, ReadinessPageCopy> = {
  en: {
    pageTitle: 'Are You Ready After Loss?',
    introHeading: 'Understanding Pet Loss & Grief',
    introText:
      'Every grief journey is unique, and there is no right timeline for welcoming another dog into your life. This short quiz is designed to help you reflect on where you are in your journey so Paction can connect you with resources and guidance that best support your current needs.',
    introImageUrl: RAINBOW_BRIDGE_IMAGE_URL,
    stepsVisible: true,
    stepsHeading: '5 Areas of Reflection',
    stepLabels: ['Timing', 'Emotions', 'Motivation', 'Household', 'Next steps'],
    whatsNextTitle: 'What’s next',
    disclaimer:
      'This reflection is for support and education only and is not a substitute for professional mental-health care. If you are struggling, the APLB and licensed counsellors are here to help.'
  },
  fr: {
    pageTitle: 'Êtes-vous prêt(e) après une perte ?',
    introHeading: "Comprendre la perte et le deuil d'un animal",
    introText:
      "Chaque deuil est unique et il n'y a pas de bon moment pour accueillir un nouveau chien dans votre vie. Ce court questionnaire vous aide à réfléchir à l'étape où vous en êtes, afin que Paction puisse vous orienter vers les ressources et l'accompagnement qui répondent le mieux à vos besoins actuels.",
    introImageUrl: RAINBOW_BRIDGE_IMAGE_URL,
    stepsVisible: true,
    stepsHeading: '5 thèmes de réflexion',
    stepLabels: [
      'Le moment',
      'Les émotions',
      'La motivation',
      'Le foyer',
      'La suite'
    ],
    whatsNextTitle: 'Et ensuite',
    disclaimer:
      "Cette réflexion vise le soutien et l'information seulement et ne remplace pas des soins professionnels en santé mentale. Si vous traversez une période difficile, l'APLB et des conseillers agréés sont là pour vous aider."
  }
};

export const DEFAULT_OUTCOME_CONTENT: Record<
  PetLossLocale,
  Record<OutcomeKey, OutcomeContent>
> = {
  en: {
    soon: {
      ...DEFAULT_OUTCOME_COPY.soon,
      callout: null,
      whatsNext: [
        {
          title: 'Connect with Pet Loss Grief Specialists',
          category: 'Support',
          url: APLB_URL,
          imageUrl: `${S3}/accountPhotos/cropped-640fc764-ade3-4cbc-bce9-6be4c294eb59.webp`
        },
        {
          title: 'Pet Loss Support Topics',
          category: 'Support',
          url: APLB_URL,
          imageUrl: `${S3}/accountPhotos/cropped-9a1d35a6-6fc4-48c2-94ee-dde5e1974618.webp`
        }
      ]
    },
    foster: {
      ...DEFAULT_OUTCOME_COPY.foster,
      callout: {
        title: 'Lower-pressure ways to help a pet in need',
        text: "Local rescues and shelters always need foster and adoptive homes. And if you'd like to talk it through first, APLB's Pet Loss Grief Specialists are here for you.",
        logoUrl: null,
        links: [{ label: 'Talk it through with APLB', url: APLB_URL }]
      },
      whatsNext: [
        {
          title: 'APLB grief resources',
          category: 'Support',
          url: APLB_URL,
          imageUrl: null
        },
        {
          title: 'Dog Breed Matchmaker',
          category: 'Tool',
          url: '/dog-breed-matchmaker',
          imageUrl: `${S3}/common/matchmaker.webp`
        },
        {
          title: 'Responsible Dog Ownership Series',
          category: 'Reflect',
          url: '/responsible-dog-ownership-series',
          imageUrl: `${S3}/common/related_ethical_breeders.webp`
        }
      ]
    },
    ready: {
      ...DEFAULT_OUTCOME_COPY.ready,
      callout: {
        title: "You don't have to leave support behind",
        text: "Even when you're ready, grief can resurface. APLB's community is here whenever you need it.",
        logoUrl: APLB_LOGO_URL,
        links: [{ label: 'APLB community & support', url: APLB_URL }]
      },
      whatsNext: [
        {
          title: 'Search Verified Breeders on Paction',
          category: 'Search',
          url: '/puppies',
          imageUrl: `${S3}/common/related_all_pups.webp`
        },
        {
          title: 'Paws & Plan Quiz',
          category: 'Quiz',
          url: '/paws-and-plan-quiz',
          imageUrl: `${S3}/common/related_expert_hub.webp`
        },
        {
          title: 'The Ultimate Dog Cost Calculator',
          category: 'Calculator',
          url: '/dog-cost-calculator',
          imageUrl: `${S3}/common/related_cost_calculator.webp`
        }
      ]
    }
  },
  fr: {
    soon: {
      ...DEFAULT_OUTCOME_COPY_FR.soon,
      callout: null,
      whatsNext: [
        {
          title: 'Parler à des spécialistes du deuil animalier',
          category: 'Soutien',
          url: APLB_URL,
          imageUrl: `${S3}/accountPhotos/cropped-640fc764-ade3-4cbc-bce9-6be4c294eb59.webp`
        },
        {
          title: 'Thèmes de soutien au deuil animalier',
          category: 'Soutien',
          url: APLB_URL,
          imageUrl: `${S3}/accountPhotos/cropped-9a1d35a6-6fc4-48c2-94ee-dde5e1974618.webp`
        }
      ]
    },
    foster: {
      ...DEFAULT_OUTCOME_COPY_FR.foster,
      callout: {
        title: "Des façons plus douces d'aider un animal dans le besoin",
        text: "Les refuges et les organismes de sauvetage locaux ont toujours besoin de familles d'accueil et d'adoption. Et si vous souhaitez d'abord en parler, les spécialistes du deuil animalier de l'APLB sont là pour vous.",
        logoUrl: null,
        links: [{ label: "En parler avec l'APLB", url: APLB_URL }]
      },
      whatsNext: [
        {
          title: "Ressources de l'APLB sur le deuil",
          category: 'Soutien',
          url: APLB_URL,
          imageUrl: null
        },
        {
          title: 'Outil de jumelage de races',
          category: 'Outil',
          url: '/dog-breed-matchmaker',
          imageUrl: `${S3}/common/matchmaker.webp`
        },
        {
          title: "Série sur la possession responsable d'un chien",
          category: 'Réflexion',
          url: '/responsible-dog-ownership-series',
          imageUrl: `${S3}/common/related_ethical_breeders.webp`
        }
      ]
    },
    ready: {
      ...DEFAULT_OUTCOME_COPY_FR.ready,
      callout: {
        title: "Vous n'avez pas à laisser le soutien derrière vous",
        text: "Même lorsque vous êtes prêt(e), le deuil peut refaire surface. La communauté de l'APLB est là chaque fois que vous en avez besoin.",
        logoUrl: APLB_LOGO_URL,
        links: [{ label: "Communauté et soutien de l'APLB", url: APLB_URL }]
      },
      whatsNext: [
        {
          title: 'Trouver un éleveur vérifié sur Paction',
          category: 'Recherche',
          url: '/puppies',
          imageUrl: `${S3}/common/related_all_pups.webp`
        },
        {
          title: 'Questionnaire Paws & Plan',
          category: 'Questionnaire',
          url: '/paws-and-plan-quiz',
          imageUrl: `${S3}/common/related_expert_hub.webp`
        },
        {
          title: "Le calculateur ultime du coût d'un chien",
          category: 'Calculateur',
          url: '/dog-cost-calculator',
          imageUrl: `${S3}/common/related_cost_calculator.webp`
        }
      ]
    }
  }
};

const imageUrlsOf = (content: OutcomeContent): string[] => [
  ...(content.callout?.logoUrl ? [content.callout.logoUrl] : []),
  ...content.whatsNext.flatMap((item) => (item.imageUrl ? [item.imageUrl] : []))
];

export const DEFAULT_CONTENT_IMAGE_URLS: ReadonlySet<string> = new Set([
  RAINBOW_BRIDGE_IMAGE_URL,
  ...Object.values(DEFAULT_OUTCOME_CONTENT).flatMap((outcomes) =>
    OUTCOME_KEYS.flatMap((key) => imageUrlsOf(outcomes[key]))
  )
]);

export const isExternalHref = (href: string): boolean =>
  /^https?:\/\//i.test(href);

export type PageCopySource = {
  locale: PetLossLocale;
  introHeading: string | null;
  introText: string | null;
  introImageUrl: string | null;
  pageCopy: ReadinessPageOverrides | null | undefined;
};

export function resolvePageCopy(source: PageCopySource): ReadinessPageCopy {
  const base = DEFAULT_PAGE_COPY[source.locale];
  const overrides = source.pageCopy ?? {};
  return {
    pageTitle: overrides.pageTitle?.trim() || base.pageTitle,
    introHeading: source.introHeading?.trim() || base.introHeading,
    introText: source.introText?.trim() || base.introText,
    introImageUrl: source.introImageUrl || base.introImageUrl,
    stepsVisible: overrides.stepsVisible ?? base.stepsVisible,
    stepsHeading: overrides.stepsHeading?.trim() || base.stepsHeading,
    stepLabels: overrides.stepLabels ?? base.stepLabels,
    whatsNextTitle: overrides.whatsNextTitle?.trim() || base.whatsNextTitle,
    disclaimer: overrides.disclaimer?.trim() || base.disclaimer
  };
}

export function resolveOutcomeContent(
  key: OutcomeKey,
  overrides: OutcomeCopyOverrides | null | undefined,
  locale: PetLossLocale
): OutcomeContent {
  const base = DEFAULT_OUTCOME_CONTENT[locale][key];
  const override = overrides?.[key];
  return {
    badge: override?.badge?.trim() || base.badge,
    heading: override?.heading?.trim() || base.heading,
    body: override?.body?.trim() || base.body,
    callout: override?.callout !== undefined ? override.callout : base.callout,
    whatsNext: override?.whatsNext ?? base.whatsNext
  };
}

export function contentImageUrls(content: {
  introImageUrl: string | null;
  outcomes: OutcomeCopyOverrides;
}): string[] {
  const urls: string[] = [];
  if (content.introImageUrl) urls.push(content.introImageUrl);
  for (const key of OUTCOME_KEYS) {
    const override = content.outcomes[key];
    if (override?.callout?.logoUrl) urls.push(override.callout.logoUrl);
    for (const item of override?.whatsNext ?? []) {
      if (item.imageUrl) urls.push(item.imageUrl);
    }
  }
  return urls;
}
